import { useLayoutEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useGetNewsByPathQuery } from 'app/api/gatewayApiSlice';
import './newsDetail.scss';
// eslint-disable-next-line import/no-extraneous-dependencies
import DOMPurify from 'dompurify';
import LoaderSpinner from 'components/atoms/Loader/LoaderSpinner';
import MetaDecorator from 'components/layout/MetaDecorator';
import { FallbackImage } from '@rcw/ui';

const BANNER_RESOURCE_TYPE_ID = 4;

// Drops the stable scrollbar gutter on this non-scrolling page and pads
// header/main/footer by its width so content stays put (see newsDetail.scss).
function useNoGutter() {
  useLayoutEffect(() => {
    const root = document.documentElement;
    const gutter = window.innerWidth - document.body.clientWidth;
    if (gutter <= 0) return undefined;
    root.style.setProperty('--news-404-gutter', `${gutter}px`);
    root.classList.add('news-404');
    return () => {
      root.classList.remove('news-404');
      root.style.removeProperty('--news-404-gutter');
    };
  }, []);
}

function NewsNotFound() {
  useNoGutter();
  return (
    <>
      <Helmet>
        <title>News not found</title>
        <meta name='robots' content='noindex' />
      </Helmet>
      <section className='flex-1 md:py-[55px] py-[40px] flex items-center justify-center text-center article-header'>
        <div className='mx-auto px-[15px]'>
          <p className='text-[18px] md:text-[20px] font-bold text-[#666] tracking-[1px] uppercase font-sans'>
            404
          </p>
          <h1 className='text-[#333] mt-[10px] mb-[10px] page-title lg:text-[42px] text-[34px]'>
            News not found
          </h1>
          <p className='mt-[20px] text-[16px] md:text-[18px] text-[#666]'>
            The article you are looking for does not exist or is no longer available.
          </p>
          <div className='flex items-center justify-center'>
            <div className='w-[95px] h-[4px] mt-[25px] bg-[#6fc]' />
          </div>
          <Link to='/news' className='inline-block mt-[30px] text-[16px] font-bold uppercase tracking-[1px]'>
            Back to news
          </Link>
        </div>
      </section>
    </>
  );
}

export function NewsDetail() {
  const { id: slug } = useParams();

  const { data, isLoading, error } = useGetNewsByPathQuery(slug, {
    skip: !slug,
  });

  if (isLoading) return <LoaderSpinner />;
  if (error?.status === 404 || (!error && !data)) return <NewsNotFound />;
  if (error) return <div>Error loading news</div>;

  const heroImage = data?.newsResources?.find(
    (resource) => resource.newsResourceTypeId === BANNER_RESOURCE_TYPE_ID,
  )?.url ?? null;
  const cleanHtml = DOMPurify.sanitize(data?.body, {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: [
      'style',
      'allow',
      'allowfullscreen',
      'frameborder',
      'scrolling',
      'src',
      'width',
      'height',
    ],
    KEEP_CONTENT: true,
  });

  return (
    <>
      <MetaDecorator
        domain={process.env.REACT_APP_PUBLIC_URL}
        title={data?.title}
        description={data?.subtitle}
        link={`news/${slug}`}
        imageCard={heroImage}
      />

      <div className='relative'>

        <div className='article-content'>
          <FallbackImage src={heroImage} alt={data?.title} fetchPriority='high' />
        </div>
        <div className='md:mt-[55px] mt-[50px]'>
          <section className='md:py-[55px] py-[40px] flex items-center justify-center text-center article-header'>
            <div className='mx-auto px-[15px] mt-[30px]'>

              <h1 className=' text-[#333] mb-[10px] page-title lg:text-[42px] text-[34px]'>
                {data?.title}
              </h1>

              {data?.subtitle && (
              <p className='mt-[20px] text-[18px] md:text-[20px] mb-[-5px] font-bold text-[#666] tracking-[1px] uppercase font-sans'>
                {data.subtitle}
              </p>
              )}
              <div className='flex items-center justify-center'>
                <div className='w-[95px] h-[4px] mt-[25px] bg-[#6fc] ' />
              </div>

            </div>
          </section>
        </div>

        <div className='container-fluid'>
          <div className='mx-auto max-w-[1300px]'>
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: cleanHtml }}
              className='news-article-body'
            />
          </div>
        </div>
      </div>
    </>
  );
}
